import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function GuardsPage(): Node {
  return html`
    <div>
      <h1>Permission Guards</h1>
      <p>Control UI visibility and route access based on user permissions using <code>setPermissions</code>, <code>hasPermission</code>, <code>guard</code>, and <code>guardedNode</code>.</p>

      <h2>Setup Permissions</h2>
      ${CodeBlock(`import { setPermissions } from 'onefold/guard';

// Set after login — pass the user's permission list
setPermissions(['read:users', 'write:posts', 'admin:settings']);`)}

      <h2>Check Permissions</h2>
      ${CodeBlock(`import { hasPermission } from 'onefold/guard';

// Returns a reactive boolean signal
hasPermission('admin:settings')  // true
hasPermission('delete:users')    // false`)}

      <h2>guardedNode</h2>
      <p>Conditionally render UI based on permissions:</p>
      ${CodeBlock(`import { html } from 'onefold';
import { guardedNode } from 'onefold/guard';

function AdminPanel(): Node {
  return html\`
    <div>
      <h2>Admin Panel</h2>
      \${guardedNode('admin:settings', () => html\`
        <button>Delete All Users</button>
        <button>Reset Database</button>
      \`)}
      \${guardedNode('write:posts', () => html\`
        <button>Create Post</button>
      \`)}
    </div>
  \`;
}`)}

      <h2>Route Guards</h2>
      <p>Protect entire routes with the <code>guard</code> wrapper:</p>
      ${CodeBlock(`import { Router, navigate } from 'onefold';
import { guard } from 'onefold/guard';

const App = Router([
  { path: '/', view: () => Home() },
  { path: '/admin', view: guard('admin:settings', () => AdminPage(), () => {
    navigate('/unauthorized');
  })},
  { path: '/posts/new', view: guard('write:posts', () => CreatePost()) },
]);`)}

      ${Callout('Permissions are reactive. If you call setPermissions() with a new list (e.g., after role change), guarded nodes update automatically.')}

      <h2>API</h2>
      <table>
        <tr><th>Function</th><th>Parameters</th><th>Description</th></tr>
        <tr><td><code>setPermissions</code></td><td>(perms: string[])</td><td>Set the active permission list.</td></tr>
        <tr><td><code>hasPermission</code></td><td>(perm: string)</td><td>Check if permission exists (reactive).</td></tr>
        <tr><td><code>guard</code></td><td>(perm, view, onDeny?)</td><td>Protect a route view function.</td></tr>
        <tr><td><code>guardedNode</code></td><td>(perm, content)</td><td>Conditionally render based on permission.</td></tr>
      </table>

      <h2>Try It</h2>
      <p>Toggle permissions to see guarded sections appear/disappear reactively:</p>

      ${Playground(`import { html, mount, createSignal } from 'onefold';
import { setPermissions, hasPermission, guardedNode } from 'onefold/guard';

function App() {
  const perms = createSignal(new Set(['read', 'write']));

  // Wire permissions to the guard system
  setPermissions(perms);

  function togglePerm(perm) {
    perms.set(prev => {
      const next = new Set(prev);
      if (next.has(perm)) next.delete(perm);
      else next.add(perm);
      return next;
    });
  }

  const allPerms = ['read', 'write', 'admin', 'billing', 'delete'];

  return html\`
    <div>
      <h3>RBAC Guards — Live Demo</h3>

      <div style="margin-bottom:16px">
        <p style="font-size:13px;font-weight:600;margin-bottom:8px">Active permissions:</p>
        <div style="display:flex;gap:6px;flex-wrap:wrap">
          \${allPerms.map(p => html\`
            <button
              onclick=\${() => togglePerm(p)}
              style=\${() => hasPermission(p)
                ? 'background:#4f46e5;color:white;border-color:#4f46e5'
                : 'background:#f1f5f9;color:#64748b'}
            >\${p}</button>
          \`)}
        </div>
      </div>

      <div style="display:grid;gap:8px">
        <div style="padding:12px;border:1px solid #e5e7eb;border-radius:8px">
          <strong>Read section</strong> (requires: read)
          \${() => guardedNode(['read'],
            () => html\`<p style="color:#166534;margin-top:4px">✓ You can see this content</p>\`,
            () => html\`<p style="color:#991b1b;margin-top:4px">✗ Access denied — need "read" permission</p>\`
          )}
        </div>
        <div style="padding:12px;border:1px solid #e5e7eb;border-radius:8px">
          <strong>Admin panel</strong> (requires: admin)
          \${() => guardedNode(['admin'],
            () => html\`<p style="color:#166534;margin-top:4px">✓ Admin panel visible</p>\`,
            () => html\`<p style="color:#991b1b;margin-top:4px">✗ Access denied — need "admin" permission</p>\`
          )}
        </div>
        <div style="padding:12px;border:1px solid #e5e7eb;border-radius:8px">
          <strong>Danger zone</strong> (requires: admin + delete)
          \${() => guardedNode(['admin', 'delete'],
            () => html\`<p style="color:#166534;margin-top:4px">✓ Delete button shown</p>\`,
            () => html\`<p style="color:#991b1b;margin-top:4px">✗ Need both "admin" and "delete" permissions</p>\`
          )}
        </div>
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'RBAC Guards — Toggle Permissions')}
    </div>
  `;
}
